import { logRealtime } from './realtime-logger.js';

type ChannelAuthorizationData = {
  auth: string;
  channel_data?: string;
};

type AuthorizeCallback = (
  error: Error | null,
  data: ChannelAuthorizationData | null,
) => void;

type PusherAuthorizerChannel = {
  name: string;
};

type PusherAuthorizer = {
  authorize: (socketId: string, callback: AuthorizeCallback) => void;
};

function maskToken(token: string | undefined): string | null {
  if (!token) return null;
  if (token.length <= 12) return '***';
  return `${token.slice(0, 8)}…${token.slice(-4)}`;
}

/**
 * Custom Pusher authorizer — logs full `/broadcasting/auth` request + response.
 * Used instead of default ajax auth for easier debugging of 4009 errors.
 */
export function createBroadcastAuthorizer(
  apiBase: string,
  getToken: () => string | undefined,
): (channel: PusherAuthorizerChannel) => PusherAuthorizer {
  const authUrl = `${apiBase.replace(/\/$/, '')}/broadcasting/auth`;

  return (channel) => ({
    authorize: (socketId, callback) => {
      const token = getToken();
      const channelName = channel.name;

      logRealtime('auth', 'POST /broadcasting/auth → request', {
        url: authUrl,
        socketId,
        channelName,
        hasToken: Boolean(token),
        tokenPreview: maskToken(token),
      });

      const body = new URLSearchParams({
        socket_id: socketId,
        channel_name: channelName,
      });

      void fetch(authUrl, {
        method: 'POST',
        credentials: 'omit',
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/x-www-form-urlencoded',
          'X-Requested-With': 'XMLHttpRequest',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: body.toString(),
      })
        .then(async (response) => {
          const rawText = await response.text();
          let parsed: unknown = rawText;
          try {
            parsed = rawText ? JSON.parse(rawText) : null;
          } catch {
            // keep text
          }

          logRealtime(
            'auth',
            `POST /broadcasting/auth ← response (${response.status})`,
            {
              ok: response.ok,
              status: response.status,
              channelName,
              body: parsed,
              hint:
                response.status === 403
                  ? 'Broadcast channel denied — on stage/prod visitor must exist (thread_id assigned after first message or from login). WS connects after thread_id is set.'
                  : undefined,
            },
            response.ok ? 'info' : 'error',
          );

          if (!response.ok) {
            callback(
              new Error(
                `[Teo Widget] Broadcast auth failed (${response.status}) for ${channelName}`,
              ),
              null,
            );
            return;
          }

          callback(null, parsed as ChannelAuthorizationData);
        })
        .catch((error: unknown) => {
          logRealtime('auth', 'POST /broadcasting/auth ← network error', {
            channelName,
            error,
          }, 'error');
          callback(
            error instanceof Error ? error : new Error(String(error)),
            null,
          );
        });
    },
  });
}
